import { getUuid } from '@front10/helpers/dist/uuid';
import * as logger from 'loglevel';
import { getWeekDayName } from '../../../helpers/localization/localizationUtils';
import { annotateAndLogError } from '../../../helpers/error';
import { encodeUrlQueryData, isNotNil } from '../../../helpers/utils';
import { requestLocations, requestRoute, requestStations } from './requests';

function validateRequest(validationFn: $TSFixMe, request: $TSFixMe, reject: $TSFixMe) {
  return request.then(validationFn).then((validation: $TSFixMe) => {
    if (validation.isValid) {
      return validation.data;
    }
    logger.error(validation.message, validation.data);
    reject(validation.data);
    return validation.data;
  });
}

interface BuildInterlinkParams {
  id?: string | number;
  baseUrl: string;
  pageType: string;
  siteEdition: string;
}

export function buildInterlink({ id, baseUrl, pageType, siteEdition }: BuildInterlinkParams) {
  const query = encodeUrlQueryData({
    orig: id,
    dest: id,
    tc: pageType,
    edition: siteEdition
  });
  return `${baseUrl}/redirect?${query}`;
}

interface InterlinkingParams {
  baseUrl: string;
  siteEdition: string;
  showOrigin: boolean;
  showDestination: boolean;
  busStations: Station[];
  filter: $TSFixMe;
}

export function interlinking({
  baseUrl,
  siteEdition,
  showOrigin,
  showDestination,
  busStations,
  filter
}: InterlinkingParams) {
  if (busStations.length < 1 || (showOrigin && showDestination)) {
    return [];
  }

  const station = busStations[0];
  const options = {
    id: station.iataCode,
    baseUrl,
    siteEdition
  };
  const links = [];
  if (filter.length === 1 && filter[0] === 'STATIONS') {
    links.push({ name: station.city.name, url: buildInterlink({ ...options, pageType: 'fci' }) });
  }
  if (filter.length === 1 && (filter[0] === 'STATIONS' || filter[0] === 'CITIES')) {
    links.push({ name: station.state.name, url: buildInterlink({ ...options, pageType: 'fs' }) });
  }

  return links;
}

interface DeepLinkParams {
  baseUrl: string;
  siteEdition: string;
  station: Station;
}

export function deepLink({ baseUrl, siteEdition, station }: DeepLinkParams) {
  const query = {
    orig: station,
    dest: station,
    tc: 'fa',
    edition: siteEdition
  };
  return `${baseUrl}/redirect?${encodeUrlQueryData(query)}`;
}

function validateLocations(response: $TSFixMe) {
  const { data } = response;
  if (!data) {
    return {
      isValid: false,
      message: 'INTEGRATION__GREYHOUND__LOCATION: locations must be an Array',
      data
    };
  }
  const keys = Object.keys(data);
  const locations = keys.length > 0 && data[keys[0]];
  if (locations && Array.isArray(locations) && locations.length > 0) {
    const location = locations[0];
    if ((!location.geoId && !location.iataCode) || !location.name) {
      return {
        isValid: false,
        message:
          'INTEGRATION__GREYHOUND__LOCATION: SCHEMA is invalid, please check API documentation.',
        data
      };
    }
  }
  return {
    isValid: true,
    data: response
  };
}

type SearchCode = 'iataCode' | 'cityGeoId' | 'stateGeoId';
/**
 * @param searchCode 'iataCode' | 'cityGeoId' | 'stateGeoId'
 * @returns 'stationMode' | 'cityMode' | 'stateMode'
 */
function normalizeCategory(searchCode: SearchCode) {
  if (searchCode === 'iataCode') {
    return 'stationMode';
  }
  if (searchCode === 'cityGeoId') {
    return 'cityMode';
  }
  if (searchCode === 'stateGeoId') {
    return 'stateMode';
  }
  return undefined;
}

export function getLocationFromStation(location: $TSFixMe, station: $TSFixMe, category?: string) {
  if (!station && location && !location.name && location.fallbackName !== -1) {
    return { ...location, label: location.fallbackName, name: location.fallbackName };
  }
  if (!location || location.name || !station) {
    return location;
  }
  let locationName = station.name;
  const searchType = category || normalizeCategory(location.searchCode);
  if (searchType === 'stationMode') {
    locationName = station.name;
  } else if (searchType === 'cityMode' || searchType === 'routeMode') {
    locationName = `${station.city.name}, ${station.state.name}`;
  } else if (searchType === 'stateMode') {
    locationName = station.state.name;
  }
  return { ...location, name: locationName, label: locationName };
}

interface State {
  geoId: number;
  name: string;
}

interface City {
  geoId: number;
  name: string;
}

interface RawLocation {
  name?: string;
  iataCode?: string;
  geoId: number;
  defaultCityId: number;
  state: State;
  city: City;
}

export function transformLocation(item: RawLocation, category?: string) {
  if (!item) {
    return undefined;
  }
  let { name } = item;
  const defaultCityId = item.geoId === -1 && item.defaultCityId;
  if ((category === 'cities' || defaultCityId) && item.state && item.state.name) {
    name = `${name}, ${item.state.name}`;
  }

  let geoId;
  if (category === 'cities') {
    geoId = item.city && item.city.geoId;
  } else if (category === 'states') {
    geoId = item.state && item.state.geoId;
  } else if (category === 'stations') {
    geoId = item.iataCode;
  }
  const locationData: any = {
    ...item,
    value: defaultCityId || item.iataCode || item.geoId,
    proximityGeoId: category === 'stations' ? item.iataCode : item.geoId,
    iataCode: item.iataCode,
    label: name,
    geoId: geoId || item.geoId,
    ...(category && { searchCode: category, parent: category }),
    ...(defaultCityId && { geoId: defaultCityId, searchCode: 'cityGeoId' })
  };

  if (locationData['originCityName']) {
    delete locationData['originCityName'];
  }
  if (locationData['originStateName']) {
    delete locationData['originStateName'];
  }

  if (locationData['destinationCityName']) {
    delete locationData['destinationCityName'];
  }
  if (locationData['destinationStateName']) {
    delete locationData['destinationStateName'];
  }
  return locationData;
}

function transformLocations(data: $TSFixMe, keys: string[]) {
  const result: $TSFixMe = [];

  keys
    .map((key) => key.toLowerCase())
    .forEach((key) => {
      if (data[key] && data[key].length > 0) {
        result.push({
          label: key,
          category: true
        });
        data[key].forEach((item: $TSFixMe) => {
          result.push(transformLocation(item, key));
        });
      }
    });
  return result;
}

export function getLocations({
  integrationSettings,
  options,
  request = requestLocations,
  meta,
  pageContext
}: $TSFixMe) {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__GREYHOUND__LOCATION: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v2GreyHoundLocationSearch: locationSettings } = integrationSettings;
    if (
      !locationSettings ||
      !locationSettings.method ||
      !locationSettings.endPoint ||
      !locationSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__GREYHOUND__LOCATION: **integrationSettings.locationApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    validateRequest(
      validateLocations,
      request({ locationSettings, options, integrationSettings, meta, pageContext }),
      reject
    )
      .then((result: $TSFixMe) => resolve(transformLocations(result.data, options.outputCategory)))
      .catch((err: $TSFixMe) => {
        const error = `INTEGRATION__GREYHOUND__LOCATION: ${err}`;
        logger.error('INTEGRATION__GREYHOUND__LOCATION:', err);
        reject(error);
      });
  });
}

function areEqualHours(hours1: $TSFixMe[], hours2: $TSFixMe[]) {
  if (!hours1 || !hours2) {
    return false;
  }
  if (hours1.length !== hours2.length) {
    return false;
  }
  return hours1.every(
    (value1, index) => value1.open === hours2[index].open && value1.close === hours2[index].close
  );
}

function transformHours(rawHours: $TSFixMe, holidays: $TSFixMe, translate: TranslateFn) {
  const result = [];
  const hoursList = Object.keys(rawHours).map((index) => rawHours[index]);
  // Make Sunday (first day) to be last
  hoursList.push(hoursList.shift());
  for (let i = 0; i < hoursList.length && i < 7; i += 1) {
    const prev = result[result.length - 1];
    const currentDay = (i + 1) % 7;
    if (prev && areEqualHours(prev.hours, hoursList[i])) {
      prev.endDay = currentDay;
    } else {
      result.push({
        hours: hoursList[i],
        startDay: currentDay,
        endDay: currentDay
      });
    }
  }
  if (holidays.length !== 0) {
    result.push({
      startDay: -1,
      endDay: -1,
      label: translate('cmp-bus-directory-map-operation-day-holidays', 'Holidays'),
      hours: holidays
    });
  }
  return result
    .filter((item) => item.hours && item.hours.length !== 0)
    .map(({ hours, ...item }) => {
      const label =
        item.startDay === item.endDay
          ? getWeekDayName(item.startDay, 'long', translate)
          : `${getWeekDayName(item.startDay, 'long', translate)} - ${getWeekDayName(
              item.endDay,
              'long',
              translate
            )}`;
      let hoursDisplay = hours;
      if (hours && hours[0]) {
        if (hours[0].open === '12:00 AM' && hours[0].close === '11:59 PM') {
          hoursDisplay = [translate('cmp-bus-directory-map-24-hours-operation', 'Open 24 hours')];
        } else if (hours[0].open === '12:00 AM' && hours[0].close === '12:00 AM') {
          hoursDisplay = [translate('cmp-bus-directory-map-close-operation', 'Closed')];
        }
      }
      return {
        label,
        hours: hoursDisplay,
        ...item
      };
    });
}

function getTooltipInfoFromStation(station: Station) {
  return station
    ? {
        title: station.title,
        description: station.address,
        link: station.directionsLink,
        stationLink: station.moreInformationLink
      }
    : {};
}

function transformPoints(busStations: Station[]) {
  return busStations.map((station) => ({
    index: station.index,
    latitude: station.location.latitude,
    longitude: station.location.longitude,
    info: getTooltipInfoFromStation(station),
    extraInfo: station
  }));
}

function validateRoute(response: $TSFixMe) {
  const validation = {
    isValid: true,
    message: '',
    data: response.data
  };
  if (
    response.data &&
    !response.data.routes &&
    !Array.isArray(response.data.routes) &&
    response.data.routes.length === 0
  ) {
    validation.isValid = false;
    validation.message =
      'INTEGRATION__GREYHOUND__BUS_STATION: route SCHEMA is invalid, please check API documentation.';
  } else {
    const { routes } = response.data;
    const route = routes[0];
    if (
      !route.geometry ||
      !route.geometry.coordinates ||
      !Array.isArray(route.geometry.coordinates)
    ) {
      validation.isValid = false;
      validation.message =
        'INTEGRATION__GREYHOUND__BUS_STATION: route SCHEMA is invalid, please check API documentation.';
    }
  }
  return validation;
}

function isValidHours(hours: Record<string, unknown>) {
  return hours && Object.keys(hours).length <= 7;
}

function validateStations(response: $TSFixMe) {
  const stations = response.data;
  if (!stations || !Array.isArray(stations)) {
    return {
      isValid: false,
      message: 'INTEGRATION__GREYHOUND__BUS_STATION: stations must be an Array',
      data: stations
    };
  }
  if (stations.length > 0) {
    const station = stations[0];
    if (
      !station.extraInfo ||
      !station.extraInfo.metaData ||
      !station.extraInfo.metaData.hours ||
      !isValidHours(station.extraInfo.metaData.hours.hoursBusStation) ||
      !isValidHours(station.extraInfo.metaData.hours.hoursBusTicketingOffice) ||
      !isValidHours(station.extraInfo.metaData.hours.hoursPackageExpress)
    ) {
      return {
        isValid: false,
        message:
          'INTEGRATION__GREYHOUND__BUS_STATION: SCHEMA is invalid, please check API documentation.',
        data: stations
      };
    }
  }
  return {
    isValid: true,
    data: stations
  };
}

/**
 * Add prefix to Phone Numbers that have at least 7 digits e.g: (XXX) XXX-XXXXX
 * If the provided number has characters other than digits we assume it is
 * already formatted and let it unchanged.
 * @param {Object} telephones
 */
const transformTelephoneNumbers = (telephones: Record<string, string>) => {
  const newPhones = { ...telephones };
  Object.keys(newPhones).forEach((key) => {
    if (newPhones[key] && typeof newPhones[key] === 'string') {
      newPhones[key] = newPhones[key].replace(/^(\d{3})(\d{3})(\d+)$/, '($1) $2-$3');
    }
  });
  return newPhones;
};

interface Station {
  index: number;
  title: string;
  address: string;
  directionsLink: string;
  moreInformationLink: string;
  iataCode?: number;
  city: City;
  state: State;
  location: {
    latitude: number;
    longitude: number;
  };
}

export function transformBusStations({
  stations,
  interlinkingSettings,
  directionsBaseUrl,
  translate,
  enableRoute,
  from
}: {
  stations: unknown[];
  interlinkingSettings: $TSFixMe;
  directionsBaseUrl: string;
  translate: TranslateFn;
  enableRoute: boolean;
  from: number;
}) {
  const data = stations.map(
    ({ extraInfo: { address, metaData, ...station }, iataCode, ...info }: $TSFixMe, index) => {
      const firstItem = index === 0 ? 'departure' : 'arrival';
      const {
        hours,
        phoneNumbers,
        ticketSaleNotes,
        locationDescriptionText,
        miscInfo,
        ...metaDataInfo
      } = metaData;
      const { directions, parkingInfo } = miscInfo;
      const { filter } = interlinkingSettings;
      const city = {
        name: address.cityName,
        geoId: `${station.hierarchy.cityGeo}`,
        fullName: `${address.cityName}, ${address.stateAbbreviation}`
      };
      const state = {
        name: info.state.name,
        geoId: `${station.hierarchy.adminGeo}`,
        abbreviation: address.stateAbbreviation
      };
      const showCityLink = filter.length === 1 && filter[0] === 'STATES';
      const name = station.name || '';
      const fullAddress = `${address.street}. ${city.fullName} ${address.postalCode}`;

      return {
        ...station,
        ...address,
        metaData: metaDataInfo,
        location: address,
        address: fullAddress,
        countryName: address.countryName,
        ticketSaleNotes,
        iataCode,
        directions,
        parkingInfo,
        locationDescriptionText,
        phoneNumbers: transformTelephoneNumbers(phoneNumbers),
        specialInstructions: address.specialInstructions.length > 0 && address.specialInstructions,
        moreInformationLink: deepLink({ station: iataCode, ...interlinkingSettings }),
        directionsLink: `${directionsBaseUrl}/'${address.latitude},${address.longitude}'`,
        cityLink: showCityLink
          ? buildInterlink({
              ...interlinkingSettings,
              pageType: 'ci',
              id: iataCode,
              name: city.name
            })
          : undefined,
        showCityLink,
        showSeparator: showCityLink,
        index: getUuid(),
        title:
          enableRoute || (stations.length === 1 && from === 0)
            ? name
            : `${from + index + 1}. ${name}`,
        locationType: enableRoute ? firstItem : null,
        city,
        state,
        hours: {
          hoursBusStation: transformHours(
            hours.hoursBusStation,
            hours.hoursBusStationHoliday,
            translate
          ),
          hoursBusTicketingOffice: transformHours(
            hours.hoursBusTicketingOffice,
            hours.hoursBusTicketingOfficeHoliday,
            translate
          ),
          hoursPackageExpress: transformHours(
            hours.hoursPackageExpress,
            hours.hoursPackageExpressHoliday,
            translate
          )
        }
      };
    }
  );
  return {
    data,
    points: transformPoints(data)
  };
}

const validateStationsRequest = (request: $TSFixMe, reject: $TSFixMe) =>
  validateRequest(validateStations, request, reject);

function getRoute({
  mapBoxSettings,
  start,
  end,
  request,
  integrationSettings,
  pageContext,
  meta
}: $TSFixMe) {
  return request({ mapBoxSettings, start, end, integrationSettings, pageContext, meta })
    .then(validateRoute)
    .then((validation: $TSFixMe) => {
      if (validation.isValid) {
        return validation.data.routes[0].geometry.coordinates.map((point: $TSFixMe) => ({
          longitude: point[0],
          latitude: point[1]
        }));
      }
      return {};
    })
    .catch(() => {
      logger.error(`INTEGRATION__GREYHOUND__BUS_STATION: Error getting the route.`);
      return {};
    });
}

type OutputCategory =
  | 'routes'
  | 'cities'
  | 'states'
  | 'stations'
  | 'cityGeoId'
  | 'stateGeoId'
  | 'iataCode';
type SearchType = 'location' | 'radius';

function getSearchCode(outputCategory: OutputCategory, searchType: SearchType) {
  if (searchType === 'location') {
    switch (outputCategory.toUpperCase()) {
      case 'ROUTES':
      case 'CITIES':
        return 'cityGeoId';
      case 'STATES':
        return 'stateGeoId';
      case 'STATIONS':
        return 'iataCode';
      default:
        return outputCategory;
    }
  } else {
    switch (outputCategory.toUpperCase()) {
      case 'ROUTES':
      case 'CITIES':
      case 'STATES':
      case 'CITYGEOID':
      case 'STATEGEOID':
        return 'geoFromGeoId';
      case 'STATIONS':
      case 'IATACODE':
        return 'geoFromIataCode';
      default:
        return outputCategory;
    }
  }
}

interface GetOptionsParams {
  defaultOptions: $TSFixMe;
  language: string;
  from: number;
  step: number;
  isZipSearch: boolean;
  location?: $TSFixMe;
}

function getOptions({
  defaultOptions,
  language,
  from,
  step,
  isZipSearch,
  location = undefined
}: GetOptionsParams) {
  const { searchCode, geoId } =
    location.cityGeoId && defaultOptions.default.geoField === false
      ? { searchCode: 'cities', geoId: location.cityGeoId }
      : { searchCode: location.searchCode, geoId: location.geoId };
  const customOptions = defaultOptions[searchCode]
    ? { ...defaultOptions.default, ...defaultOptions[searchCode] }
    : defaultOptions.default;
  const options = {
    ...customOptions,
    from,
    size: step,
    language
  };
  const filterFields = customOptions.filterFields ? [...customOptions.filterFields] : [];
  if ((customOptions.geoField && location) || (location && isZipSearch)) {
    options.geoField = {
      value: defaultOptions.geoValue || 50.0,
      ...options.geoField,
      name: 'geo',
      [getSearchCode(searchCode, 'radius')]: location.proximityGeoId
    };
    if (isZipSearch) {
      options.sortingDetails = [
        {
          field: 'geo',
          order: 'ASC'
        }
      ];
    }
  } else if (location) {
    delete options.geoField;
    filterFields.push({
      name: getSearchCode(searchCode, 'location'),
      values: [geoId]
    });
  }
  options.filterFields = filterFields;
  return options;
}

interface GetBusStationsParams {
  integrationSettings: IntegrationSettings;
  options: $TSFixMe[];
  enableRoute: boolean;
  interlinkingSettings: $TSFixMe;
  translate: TranslateFn;
  request: $TSFixMe;
  routeRequest: $TSFixMe;
  directionsBaseUrl: string;
  pageContext: $TSFixMe;
  meta: $TSFixMe;
}

/**
 * Retrieve bus stations from bus stations endpoint
 * @param {*} filter
 */
export function getBusStations({
  integrationSettings,
  options,
  enableRoute,
  interlinkingSettings,
  translate,
  request = requestStations,
  routeRequest = requestRoute,
  pageContext,
  meta,
  directionsBaseUrl
}: GetBusStationsParams) {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__GREYHOUND__BUS_STATION: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    if (!interlinkingSettings) {
      const MESSAGE = `INTEGRATION__GREYHOUND__BUS_STATION: **interlinkingSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const {
      v2GreyHoundStationSearch: busStationSettings,
      v5MapBoxApi: mapBoxSettings
    } = integrationSettings;
    if (
      !busStationSettings ||
      !busStationSettings.method ||
      !busStationSettings.endPoint ||
      !busStationSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__GREYHOUND__BUS_STATION: **integrationSettings.busStationApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    const stationsRequest = Promise.all(
      options.map((option) =>
        validateStationsRequest(
          request({
            ...busStationSettings,
            options: getOptions(option),
            integrationSettings,
            pageContext,
            meta
          }),
          reject
        )
      )
    ).then((res) => (enableRoute ? [res[0][0], res[1][0]].filter(isNotNil) : res[0]));

    return stationsRequest
      .then((stations) => {
        if (enableRoute && stations.length !== 2) {
          return { data: [], points: [] };
        }
        const data = transformBusStations({
          stations,
          interlinkingSettings,
          translate,
          enableRoute,
          directionsBaseUrl,
          from: enableRoute ? 0 : options[0].from
        });
        if (enableRoute) {
          return getRoute({
            request: routeRequest,
            mapBoxSettings,
            start: data.data[0].location,
            end: data.data[data.data.length - 1].location,
            integrationSettings,
            pageContext,
            meta
          }).then((res: $TSFixMe) => {
            const newData = { ...data };

            // Added info to first point and lastPoint.
            // This information is used to show in the first point/tooltip
            // and last point/tooltip that define the route
            const startRoute = data.data[0];
            const endRoute = data.data[1];
            res[0].index = data.points[0].index;
            res[0].info = getTooltipInfoFromStation(startRoute);
            res[0].extraInfo = startRoute;

            res[res.length - 1].index = data.points[data.points.length - 1].index;
            res[res.length - 1].info = getTooltipInfoFromStation(endRoute);
            res[res.length - 1].extraInfo = endRoute;

            newData.points = res;
            return newData;
          });
        }
        return data;
      })
      .then(resolve)
      .catch((err) => {
        const error = annotateAndLogError(err, 'INTEGRATION__GREYHOUND__BUS_STATION');
        reject(error);
      });
  });
}

const zipRegex = /^[0-9]{5}(?:-[0-9]{4})?$/;

export function isZipCode(value?: string) {
  if (!value) {
    return false;
  }
  return zipRegex.test(value);
}
